import { useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import toast from "react-hot-toast";

import { FormActions, FormField, FormRow, FormSection, PageHeader } from "../../components/ui";
import { useTenantScope } from "../../hooks/useTenantScope";
import {
  DEFAULT_EVENT_POINTS_MESSAGE_TEMPLATES,
  DEFAULT_POINT_CURRENCY_NAME,
  DEFAULT_REFERRAL_SHARE_TEXT,
  PointCurrencyGender,
} from "../../models/pointSettings";
import {
  useGetPointSettingsQuery,
  useUpdatePointSettingsMutation,
} from "../../store/services/point-settings";
import { useGetSupportArticlesQuery } from "../../store/services/support";
import { getAdminFacingApiErrorMessage } from "../../utils/functions/api-errors";
import { PointsPageCard, PointsPageLoading } from "../PointRules/components/PointsPageCard";
import {
  NO_RULES_ARTICLE_OPTION_VALUE,
  PointSettingsFormValues,
  buildInitialPointSettingsFormValues,
  buildRulesArticleOptions,
  buildUpdatePointSettingsPayload,
  pointSettingsValidationSchema,
  toRulesArticleId,
} from "./pointSettingsForm";

const PAGE_TITLE = "Configuración de puntos";
// Pantalla nueva: escala tipográfica §3.3 (ver layout.css).
const PAGE_CLASS = "type-scale";

const GENDER_OPTIONS: { label: string; value: PointCurrencyGender }[] = [
  { label: "Femenino", value: "f" },
  { label: "Masculino", value: "m" },
];

const DEFAULT_GENDER_LABEL =
  GENDER_OPTIONS.find((option) => option.value === DEFAULT_POINT_CURRENCY_NAME.gender)?.label ?? "";

export const PointSettings = () => {
  const tenantScope = useTenantScope();
  // Con scope global ("Todos") no hay un único tenant a configurar: hay que elegir uno en el
  // selector superior, igual que en la configuración de beneficios.
  const needsTenantSelection = !tenantScope.loading && !tenantScope.locked;

  const {
    data: settings,
    isLoading,
    isError,
  } = useGetPointSettingsQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });
  const [updateSettings, { isLoading: isSaving }] = useUpdatePointSettingsMutation();

  const {
    data: supportArticles,
    isLoading: isLoadingArticles,
    isError: isArticlesError,
  } = useGetSupportArticlesQuery(undefined, {
    skip: tenantScope.loading || needsTenantSelection,
  });

  useEffect(() => {
    if (isArticlesError) toast.error("No se pudieron cargar los artículos de Soporte");
  }, [isArticlesError]);

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    resetForm,
    touched,
    errors,
    values,
    dirty,
  } = useFormik<PointSettingsFormValues>({
    initialValues: buildInitialPointSettingsFormValues(settings ?? null),
    enableReinitialize: true,
    validationSchema: pointSettingsValidationSchema,
    onSubmit: (formValues) => {
      const payload = buildUpdatePointSettingsPayload(formValues, settings ?? null);
      if (!payload) return;
      updateSettings(payload)
        .unwrap()
        .then(() => toast.success("Configuración guardada"))
        .catch((err) => {
          toast.error(getAdminFacingApiErrorMessage(err, "No se pudo guardar la configuración"));
        });
    },
  });

  const articleOptions = useMemo(
    () =>
      buildRulesArticleOptions(supportArticles ?? [], values.rulesArticleId, tenantScope.tenantId),
    [supportArticles, values.rulesArticleId, tenantScope.tenantId]
  );

  if (tenantScope.loading || isLoading) {
    return <PointsPageLoading title={PAGE_TITLE} className={PAGE_CLASS} />;
  }

  return (
    <PointsPageCard className={PAGE_CLASS}>
      <PageHeader title={PAGE_TITLE} />

      {needsTenantSelection ? (
        <div className="flex justify-content-center p-4">
          <Message
            severity="warn"
            text="Seleccioná un tenant en el selector superior para ver y editar su configuración de puntos"
          />
        </div>
      ) : isError || !settings ? (
        // Sin el dato del servidor no se muestra el form: un valor previo podría no ser el guardado.
        <div className="flex justify-content-center p-4">
          <Message severity="error" text="No se pudo cargar la configuración" />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <FormSection title="Nombre de los puntos" defaultOpen>
            <FormRow columns={2}>
              <FormField
                label="Nombre en singular"
                htmlFor="pointCurrencySingular"
                error={touched.singular && errors.singular ? errors.singular : undefined}
              >
                <InputText
                  invalid={!!touched.singular && !!errors.singular}
                  className="w-full"
                  id="pointCurrencySingular"
                  name="singular"
                  placeholder={DEFAULT_POINT_CURRENCY_NAME.singular}
                  value={values.singular}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
              <FormField
                label="Nombre en plural"
                htmlFor="pointCurrencyPlural"
                error={touched.plural && errors.plural ? errors.plural : undefined}
              >
                <InputText
                  invalid={!!touched.plural && !!errors.plural}
                  className="w-full"
                  id="pointCurrencyPlural"
                  name="plural"
                  placeholder={DEFAULT_POINT_CURRENCY_NAME.plural}
                  value={values.plural}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <FormRow columns={2}>
              <FormField
                label="Género"
                htmlFor="pointCurrencyGender"
                error={touched.gender && errors.gender ? errors.gender : undefined}
              >
                <Dropdown
                  inputId="pointCurrencyGender"
                  value={values.gender}
                  options={GENDER_OPTIONS}
                  optionLabel="label"
                  optionValue="value"
                  placeholder={DEFAULT_GENDER_LABEL}
                  showClear
                  invalid={!!touched.gender && !!errors.gender}
                  onChange={(event) => void setFieldValue("gender", event.value ?? null)}
                  className="w-full"
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <small className="text-600">
              Así se muestra el nombre en la app. Completá los tres campos, o dejalos vacíos para
              usar «{DEFAULT_POINT_CURRENCY_NAME.plural}».
            </small>
          </FormSection>

          <FormSection title="Reglas del canje" defaultOpen>
            <FormRow columns={1}>
              <FormField label="Artículo de reglas del canje" htmlFor="pointRulesArticle">
                <Dropdown
                  inputId="pointRulesArticle"
                  value={values.rulesArticleId ?? NO_RULES_ARTICLE_OPTION_VALUE}
                  options={articleOptions}
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Sin artículo elegido"
                  filter
                  filterPlaceholder="Buscar artículo"
                  onChange={(event) =>
                    void setFieldValue("rulesArticleId", toRulesArticleId(event.value))
                  }
                  className="w-full"
                  disabled={isSaving}
                  loading={isLoadingArticles}
                />
              </FormField>
            </FormRow>

            <small className="text-600">
              El artículo de Soporte que abre la app desde el botón de reglas del canje. Sin
              artículo elegido, la app no muestra el enlace.
            </small>
          </FormSection>

          <FormSection title="Mensaje de puntos en el detalle del evento" defaultOpen>
            <FormRow columns={1}>
              <FormField
                label="Título"
                htmlFor="eventPointsTitleTemplate"
                error={
                  touched.eventPointsTitleTemplate && errors.eventPointsTitleTemplate
                    ? errors.eventPointsTitleTemplate
                    : undefined
                }
              >
                <InputText
                  invalid={!!touched.eventPointsTitleTemplate && !!errors.eventPointsTitleTemplate}
                  className="w-full"
                  id="eventPointsTitleTemplate"
                  name="eventPointsTitleTemplate"
                  placeholder={DEFAULT_EVENT_POINTS_MESSAGE_TEMPLATES.titleTemplate}
                  value={values.eventPointsTitleTemplate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <FormRow columns={1}>
              <FormField
                label="Cláusula de asistencia"
                htmlFor="eventPointsAttendanceClauseTemplate"
                error={
                  touched.eventPointsAttendanceClauseTemplate && errors.eventPointsAttendanceClauseTemplate
                    ? errors.eventPointsAttendanceClauseTemplate
                    : undefined
                }
              >
                <InputText
                  invalid={
                    !!touched.eventPointsAttendanceClauseTemplate &&
                    !!errors.eventPointsAttendanceClauseTemplate
                  }
                  className="w-full"
                  id="eventPointsAttendanceClauseTemplate"
                  name="eventPointsAttendanceClauseTemplate"
                  placeholder={DEFAULT_EVENT_POINTS_MESSAGE_TEMPLATES.attendanceClauseTemplate}
                  value={values.eventPointsAttendanceClauseTemplate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <FormRow columns={1}>
              <FormField
                label="Cláusula de bonus"
                htmlFor="eventPointsBonusClauseTemplate"
                error={
                  touched.eventPointsBonusClauseTemplate && errors.eventPointsBonusClauseTemplate
                    ? errors.eventPointsBonusClauseTemplate
                    : undefined
                }
              >
                <InputText
                  invalid={!!touched.eventPointsBonusClauseTemplate && !!errors.eventPointsBonusClauseTemplate}
                  className="w-full"
                  id="eventPointsBonusClauseTemplate"
                  name="eventPointsBonusClauseTemplate"
                  placeholder={DEFAULT_EVENT_POINTS_MESSAGE_TEMPLATES.bonusClauseTemplate}
                  value={values.eventPointsBonusClauseTemplate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <small className="text-600">
              El título admite {"{total}"} (puntos totales) y {"{currency}"} (nombre de los
              puntos). La cláusula de asistencia admite {"{attendance}"}; la de bonus,{" "}
              {"{bonus}"}. Si el evento sólo da un tipo de puntos, sólo se muestra esa cláusula.
              Dejá un campo vacío para usar el texto por defecto.
            </small>
          </FormSection>

          <FormSection title="Texto de invitación" defaultOpen>
            <FormRow columns={1}>
              <FormField
                label="Texto para compartir la invitación"
                htmlFor="referralShareText"
                error={
                  touched.referralShareText && errors.referralShareText
                    ? errors.referralShareText
                    : undefined
                }
              >
                <InputText
                  invalid={!!touched.referralShareText && !!errors.referralShareText}
                  className="w-full"
                  id="referralShareText"
                  name="referralShareText"
                  placeholder={DEFAULT_REFERRAL_SHARE_TEXT}
                  value={values.referralShareText}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={isSaving}
                />
              </FormField>
            </FormRow>

            <small className="text-600">
              El texto que acompaña al link cuando un usuario invita a un amigo desde la app (se
              muestra arriba del link). Dejalo vacío para usar «{DEFAULT_REFERRAL_SHARE_TEXT}».
            </small>
          </FormSection>

          <FormActions
            align="end"
            primaryLabel="Guardar cambios"
            loading={isSaving}
            disabled={isSaving || !dirty}
            disabledReason={!dirty ? "No hay cambios para guardar" : undefined}
            onCancel={() => resetForm()}
          />
        </form>
      )}
    </PointsPageCard>
  );
};
